import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { getAgeYears } from '@/lib/format';
import type { InterpreterRequired, SexCode } from '@/models/patient';

/** Upper bound for a typed age. Matches the pedigree date/age parser's MAX_REASONABLE_AGE, so the
 *  two entry points reject the same nonsense. */
const MAX_AGE_YEARS = 130;

/** Sex codes, in display order. `M`/`F` lead; `O`/`U` follow (PD-138). */
const SEX_OPTIONS: readonly SexCode[] = ['M', 'F', 'O', 'U', 'INTERSEX', 'UNASSIGNED'];

/** Canonical gender-identity codes accepted by the BE
 *  (`GenderOptions` enum: `unknown | M | F | O | nonBinary`). Stored on
 *  the wire as the code; rendered to the user via `GENDER_LABEL_KEY`.
 *  Existing non-canonical values (e.g. legacy free-text from earlier
 *  records) are preserved as a custom option so we never silently mutate
 *  prior data. */
export const GENDER_IDENTITY_OPTIONS = ['M', 'F', 'nonBinary', 'O', 'unknown'] as const;

/** i18n suffix for each gender code under `patient.genderIdentity.*`. Exported
 *  so other consumers (e.g. the Create Patient wizard, PTV2-1163) can render
 *  the same labels for the same codes without redeclaring the map. */
export const GENDER_LABEL_KEY: Readonly<
  Record<(typeof GENDER_IDENTITY_OPTIONS)[number], string>
> = {
  M: 'MALE',
  F: 'FEMALE',
  nonBinary: 'NON_BINARY',
  O: 'OTHER',
  unknown: 'UNKNOWN',
};

function isCanonicalGender(value: string): value is (typeof GENDER_IDENTITY_OPTIONS)[number] {
  return (GENDER_IDENTITY_OPTIONS as readonly string[]).includes(value);
}

interface TextFieldProps {
  readonly label: string;
  readonly value: string;
  readonly placeholder?: string;
  readonly onChange: (value: string) => void;
}

export function TextField({
  label,
  value,
  placeholder,
  onChange,
}: TextFieldProps): ReactElement {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <Input
        value={value}
        placeholder={placeholder}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        className="text-sm"
      />
    </label>
  );
}

interface AgeFieldProps {
  readonly label: string;
  /** Partial ISO date of birth (`YYYY`, `YYYY-MM`, `YYYY-MM-DD`) — the stored datum. */
  readonly dateOfBirth: string | null;
  readonly disabled?: boolean | undefined;
  /** Receives the new partial-ISO date of birth, or null when the age is cleared. */
  readonly onChange: (dateOfBirth: string | null) => void;
}

/**
 * PTV2-1583 — current age in years, as an alternative way in to the date of birth.
 *
 * Age is a VIEW over `dateOfBirth`, never a stored field: it is recomputed from the saved date on
 * every render, so it stays right as time passes instead of freezing at the value someone typed.
 * Typing an age writes a YEAR-ONLY date of birth — the only precision an age actually carries, and
 * one `SimpleDate` already supports — so we never invent a month and day the user did not give us.
 *
 * Primarily for relatives: a patient's exact date of birth is usually known, a relative's often
 * isn't. Editing the age of someone with a full date of birth deliberately coarsens it to the year;
 * that is the documented trade of entering an age instead of a date.
 */
export function AgeField({ label, dateOfBirth, disabled, onChange }: AgeFieldProps): ReactElement {
  const age = getAgeYears(dateOfBirth);
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <Input
        type="number"
        inputMode="numeric"
        min={0}
        max={MAX_AGE_YEARS}
        value={age === null ? '' : String(age)}
        disabled={disabled === true}
        onChange={(event) => {
          const raw = event.target.value.trim();
          if (raw.length === 0) {
            // Age and date of birth are one datum — clearing the view clears the value.
            onChange(null);
            return;
          }
          const years = Number.parseInt(raw, 10);
          if (!Number.isFinite(years) || years < 0 || years > MAX_AGE_YEARS) return;
          onChange(String(new Date().getFullYear() - years));
        }}
        className="text-sm"
      />
    </label>
  );
}

interface SelectFieldProps<T extends string> {
  readonly label: string;
  readonly value: T | null;
  readonly options: readonly T[];
  readonly renderOption: (value: T) => string;
  readonly onChange: (next: T) => void;
}

export function SelectField<T extends string>({
  label,
  value,
  options,
  renderOption,
  onChange,
}: SelectFieldProps<T>): ReactElement {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <Select
        value={value ?? ''}
        className="h-9 text-sm"
        onChange={(event) => {
          onChange(event.target.value as T);
        }}
      >
        {options.map((option) => (
          <option key={option} value={option}>
            {renderOption(option)}
          </option>
        ))}
      </Select>
    </label>
  );
}

interface InterpreterRequiredFieldProps {
  readonly label: string;
  readonly value: InterpreterRequired | null;
  readonly onChange: (next: InterpreterRequired | null) => void;
}

/**
 * Tri-state Yes / No / Unknown select with an explicit "unset" slot.
 * "Unset" maps to `null` on the wire so the BE distinguishes "never asked"
 * from "answered Unknown".
 */
export function InterpreterRequiredField({
  label,
  value,
  onChange,
}: InterpreterRequiredFieldProps): ReactElement {
  const { t } = useTranslation();
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <Select
        value={value ?? ''}
        className="h-9 text-sm"
        onChange={(event) => {
          const next = event.target.value;
          if (next === '') onChange(null);
          else onChange(next as InterpreterRequired);
        }}
      >
        <option value="">{t('patient.demographics.emptyValue')}</option>
        <option value="Y">{t('patient.demographics.interpreterYes')}</option>
        <option value="N">{t('patient.demographics.interpreterNo')}</option>
        <option value="U">{t('patient.demographics.interpreterUnknown')}</option>
      </Select>
    </label>
  );
}

interface SexFieldProps {
  readonly label: string;
  readonly value: SexCode | null;
  readonly onChange: (next: SexCode) => void;
}

/**
 * Sex as a plain dropdown so it carries the same visual weight as the
 * Life status / Gender identity selects beside it. When unset, a disabled
 * placeholder shows rather than defaulting to the first option; sex can't
 * be re-cleared once chosen, so the placeholder drops out after selection.
 */
export function SexField({ label, value, onChange }: SexFieldProps): ReactElement {
  const { t } = useTranslation();
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <Select
        value={value ?? ''}
        className="h-9 text-sm"
        onChange={(event) => {
          onChange(event.target.value as SexCode);
        }}
      >
        {value === null && (
          <option value="" disabled>
            {t('patient.demographics.emptyValue')}
          </option>
        )}
        {SEX_OPTIONS.map((option) => (
          <option key={option} value={option}>
            {t(`patient.sex.${option}`)}
          </option>
        ))}
      </Select>
    </label>
  );
}

interface GenderIdentityFieldProps {
  readonly label: string;
  readonly value: string | null;
  readonly onChange: (next: string | null) => void;
}

export function GenderIdentityField({
  label,
  value,
  onChange,
}: GenderIdentityFieldProps): ReactElement {
  const { t } = useTranslation();
  const customValue = value !== null && !isCanonicalGender(value) ? value : null;
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      <Select
        value={value ?? ''}
        className="h-9 text-sm"
        onChange={(event) => {
          const next = event.target.value;
          onChange(next === '' ? null : next);
        }}
      >
        <option value="">{t('patient.demographics.emptyValue')}</option>
        {customValue !== null && <option value={customValue}>{customValue}</option>}
        {GENDER_IDENTITY_OPTIONS.map((option) => (
          <option key={option} value={option}>
            {t(`patient.genderIdentity.${GENDER_LABEL_KEY[option]}`)}
          </option>
        ))}
      </Select>
    </label>
  );
}

